import { defineStore } from "pinia"
import { get } from "lodash"
import { HttpService } from "@/helpers/http-service"
import { CONSTANT } from "@/helpers/constant"
import { isMembershipFeaturesEnabled } from "@/helpers/membership"
import { ApiResponse } from "@/types/apiResponse"
import { ClubInvitationResponse, ClubMembersResponse, ClubMembershipCriteria, RemoveClubMemberResponse } from "@/types/club-membership"
import { useSystemAndContextStore } from "@/stores/systemAndContext.store"

const httpService = new HttpService()

const getApiErrorDetail = (error: unknown): string => {
  return get(error, "response.data.errors[0].detail", "") || get(error, "response.data.detail", "")
}

interface State {
  membersData: ClubMembersResponse | null
  invitationData: ClubInvitationResponse | null
  lastMembersCriteria: ClubMembershipCriteria | Record<string, unknown> | null
  lastInvitationCriteria: ClubMembershipCriteria | null
  membersError: string | null
  invitationError: string | null
}

export const useClubMembershipStore = defineStore({
  id: "club-membership",
  state: (): State => ({
    membersData: null,
    invitationData: null,
    lastMembersCriteria: null,
    lastInvitationCriteria: null,
    membersError: null,
    invitationError: null,
  }),
  getters: {
    members: (state) => state.membersData?.members ?? [],
    clubName: (state) => state.membersData?.clubName ?? state.invitationData?.clubName ?? "",
    membershipPoints: (state) => state.membersData?.membershipPoints ?? 0,
    teamportalPoints: (state) => state.membersData?.teamportalPoints ?? 0,
    memberCount: (state) => state.membersData?.memberCount ?? 0,
    retailPoints: (state) => state.membersData?.retailPoints ?? 0,
    cumulativePoints: (state) => state.membersData?.cumulativePoints ?? 0,
    invitation: (state) => state.invitationData,
  },
  actions: {
    isMembershipEnabled(): boolean {
      const systemAndContextStore = useSystemAndContextStore()
      return isMembershipFeaturesEnabled(systemAndContextStore.getCurrentContext)
    },

    resolveInvitationCriteria(criteria?: ClubMembershipCriteria): ClubMembershipCriteria {
      if (criteria) {
        this.lastInvitationCriteria = criteria
      }

      return this.lastInvitationCriteria ?? {}
    },

    async fetchMembers(criteria?: Record<string, unknown>): Promise<ClubMembersResponse | null> {
      if (!this.isMembershipEnabled()) {
        return null
      }

      if (criteria) {
        this.lastMembersCriteria = criteria
      }

      this.membersError = null

      try {
        const body = this.lastMembersCriteria ?? {}
        const result: ApiResponse<ClubMembersResponse> = await httpService.post(CONSTANT.API.CLUB_MEMBERSHIP.MEMBERS, body)

        if (result.status === CONSTANT.HTTP_STATUS.OK) {
          this.membersData = result.data
          return result.data
        }
      } catch (error) {
        this.membersError = getApiErrorDetail(error)
        console.error(error)
      }

      return null
    },

    async fetchInvitation(criteria?: Record<string, unknown>): Promise<ClubInvitationResponse | null> {
      if (!this.isMembershipEnabled()) {
        return null
      }

      if (criteria) {
        this.lastInvitationCriteria = criteria
      }

      this.invitationError = null

      try {
        const body = this.lastInvitationCriteria ?? criteria ?? {}
        const result: ApiResponse<ClubInvitationResponse> = await httpService.post(CONSTANT.API.CLUB_MEMBERSHIP.INVITATION, body)

        if (result.status === CONSTANT.HTTP_STATUS.OK) {
          this.invitationData = result.data
          return result.data
        }
      } catch (error) {
        this.invitationError = getApiErrorDetail(error)
        this.invitationData = null
        console.error(error)
      }

      return null
    },

    async removeMember(memberCustomerId: string): Promise<RemoveClubMemberResponse | null> {
      if (!this.isMembershipEnabled()) {
        return null
      }

      try {
        const result: ApiResponse<RemoveClubMemberResponse> = await httpService.post(CONSTANT.API.CLUB_MEMBERSHIP.REMOVE_MEMBER, {
          memberCustomerId,
        })

        if (result.status === CONSTANT.HTTP_STATUS.OK) {
          return result.data
        }
      } catch (error) {
        console.error(error)
      }

      return null
    },

    clear() {
      this.membersData = null
      this.invitationData = null
      this.lastMembersCriteria = null
      this.lastInvitationCriteria = null
      this.membersError = null
      this.invitationError = null
    },
  },
})
